/** @format */

import { AnalyticsService, UtilityService } from '@shared/data-access/services';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, computed, inject, viewChild } from '@angular/core';
import {
	homefieldIconChevronRight,
	HomefieldIconLibraryModule,
	homefieldIconSecurityVerifiedUser,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { WarrantyButtonAction, WarrantyButtonVariant } from '@pdp/data-access/interfaces';
import { selectSelectedWarrantyDetails, selectWarrantyButtonState } from '@pdp/data-access/state';

import { AttributesUtilityService } from '@pdp/attributes/data-access';
import { Store } from '@ngrx/store';
import { WarrantyButtonState } from '@pdp/data-access/enums';
import { WarrantySelectionDrawerComponent } from '../warranty-selection-drawer/warranty-selection-drawer.component';
import {
	selectAtcUserInputQty,
	warrantyATCAttemptedWithAttributeErrors
} from '@pdp/add-to-cart/data-access';

@Component({
	selector: 'pdp-component-warranty-button',
	imports: [CommonModule, HomefieldIconLibraryModule, WarrantySelectionDrawerComponent],
	templateUrl: './warranty-button.component.html',
	styleUrl: './warranty-button.component.scss'
})
export class WarrantyButtonComponent {
	homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	attributesUtilityService = inject(AttributesUtilityService);
	utilityService = inject(UtilityService);
	analyticsService = inject(AnalyticsService);
	readonly store = inject(Store);
	private readonly document = inject(DOCUMENT);

	variants: Record<WarrantyButtonState, WarrantyButtonVariant> = {
		warrantyAvailable: {
			text: 'Add a No Sweat Protection Plan',
			iconColor: 'black',
			buttonAction: 'openDrawer'
		},
		warrantyNotAvailable: {
			text: 'Protection Plan Unavailable',
			subtext: 'Select a different option to view availability',
			iconColor: 'var(--hmf-label-disabled)',
			visuallyDisabled: true,
			actuallyDisabled: true
		},
		warrantySelected: {
			text: 'Protection Plan: ',
			showWarrantyDetails: true,
			iconColor: 'var(--hmf-label-positive)',
			buttonAction: 'openDrawer'
		},
		beforeSkuResolve: {
			text: 'Select All Product Options',
			subtext: 'to view protection plan coverages',
			iconColor: 'var(--hmf-label-disabled)',
			visuallyDisabled: true,
			actuallyDisabled: false,
			buttonAction: 'selectOption'
		}
	};

	buttonState = this.store.selectSignal(selectWarrantyButtonState);

	variant = computed(() => this.variants[this.buttonState()]);

	selectionDrawer = viewChild.required(WarrantySelectionDrawerComponent);

	selectedWarrantyDetails = this.store.selectSignal(selectSelectedWarrantyDetails);
	atcUserInputQty = this.store.selectSignal(selectAtcUserInputQty);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconSecurityVerifiedUser,
			homefieldIconChevronRight
		]);
	}

	buttonClick(buttonAction: WarrantyButtonAction, event: MouseEvent) {
		event.stopPropagation(); // keep the click from being replayed by withEventReplay after the app is stable (avoids duplicate calls)
		if (buttonAction === 'selectOption') {
			this.showErrorsAndScrollToAttributes();
			this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
				event: 'warranty',
				value: 'pdpClickBeforeSkuResolve'
			});
		} else if (buttonAction === 'openDrawer') {
			this.selectionDrawer()?.open();
		}
	}

	private showErrorsAndScrollToAttributes() {
		const isErrorOnAttributes = this.attributesUtilityService.validateIfAttributeErrorsExist(
			'[warranty-button] showErrorsAndScrollToAttributes',
			false
		);
		const attributesContainer = this.document.getElementById('attributes-v2-container');

		if (!isErrorOnAttributes || !attributesContainer) {
			return;
		}

		this.utilityService.scrollToElement(attributesContainer, {
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest'
		});
		this.store.dispatch(warrantyATCAttemptedWithAttributeErrors());
	}
}
